"use client"

import { useState, useEffect, useRef, useCallback, useMemo, memo } from "react"
import { PanelRightClose, Loader2, Check } from "lucide-react"
import { TypingText } from "@/src/components/agent/typing-text"
import { RichCardRenderer } from "@/src/components/demo/rich-card-renderer"
import type { SkillDemoStep } from "@/lib/skill-demo-data"
import { PLACEHOLDER_SKILL_DEMO } from "@/lib/skill-demo-data"

/* ── Agent 头像 ── */
const DemoAgentAvatar = memo(function DemoAgentAvatar() {
  return (
    <div className="relative h-6 w-6 shrink-0 mt-0.5">
      <div className="absolute -inset-0.5 rounded-full bg-primary/20 blur-[2px]" />
      <div className="relative h-full w-full rounded-full bg-linear-to-br from-primary via-teal-500 to-emerald-400 shadow-sm overflow-hidden ring-1 ring-white/15">
        <div className="absolute top-[2px] left-[3px] h-1.5 w-1.5 rounded-full bg-white/30 blur-[1px]" />
        <svg
          className="absolute inset-0 m-auto h-2 w-2 text-white/75 drop-shadow-sm"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden
        >
          <path d="M8 0C8.5 5 11 8 16 8C11 8 8.5 11 8 16C7.5 11 5 8 0 8C5 8 7.5 5 8 0Z" />
        </svg>
      </div>
    </div>
  )
})

/* ── Tool call item with sequential reveal ── */
const DemoToolCallItem = memo(function DemoToolCallItem({ label, paramLabel, result, index }: {
  label: string; paramLabel?: string; result?: string; index: number
}) {
  const [done, setDone] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setDone(true), 800 + index * 600)
    return () => clearTimeout(t)
  }, [index])

  const style = useMemo(() => ({
    animationDelay: `${index * 200}ms`,
    animationFillMode: "both" as const,
  }), [index])

  return (
    <div
      className="flex items-center gap-2 rounded-xl px-2.5 py-1.5 bg-secondary/50 border border-border/40 shadow-sm text-caption animate-in fade-in slide-in-from-left-2 duration-300"
      style={style}
    >
      <span className="flex items-center justify-center h-4 w-4 shrink-0">
        {done ? (
          <Check className="h-3 w-3 text-emerald-500 animate-in zoom-in duration-200" />
        ) : (
          <Loader2 className="h-3 w-3 text-primary/60 animate-spin" />
        )}
      </span>
      <span className="font-medium text-foreground/90 shrink-0">{label}</span>
      {paramLabel && (
        <span className="text-muted-foreground truncate flex-1 min-w-0">→ {paramLabel}</span>
      )}
      {done && result && (
        <span className="shrink-0 text-micro text-emerald-600 dark:text-emerald-400 font-medium animate-in fade-in duration-200">{result}</span>
      )}
    </div>
  )
})

/* ── Individual step renderers — memoized to prevent re-render cascade ── */

const UserStep = memo(function UserStep({ content }: { content?: string }) {
  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary px-3.5 py-2 shadow-sm">
        <p className="text-xs text-primary-foreground leading-relaxed">{content}</p>
      </div>
    </div>
  )
})

const AgentReplyStep = memo(function AgentReplyStep({ content }: { content?: string }) {
  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex gap-2">
      <DemoAgentAvatar />
      <div className="flex-1 max-w-[85%]">
        <div className="rounded-2xl rounded-tl-sm bg-secondary/50 border border-border/40 px-3.5 py-2 shadow-sm">
          <p className="text-xs text-foreground leading-relaxed whitespace-pre-line">
            <TypingText text={content ?? ""} speed={35} />
          </p>
        </div>
      </div>
    </div>
  )
})

const ToolCallsStep = memo(function ToolCallsStep({ step }: { step: SkillDemoStep }) {
  if (!step.toolCalls) return null
  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex gap-2">
      <DemoAgentAvatar />
      <div className="flex-1 max-w-[90%] space-y-1.5">
        {step.toolCalls.map((tc, i) => (
          <DemoToolCallItem key={i} index={i} {...tc} />
        ))}
      </div>
    </div>
  )
})

const RichCardStep = memo(function RichCardStep({ step }: { step: SkillDemoStep }) {
  if (!step.richCards) return null
  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 pl-8">
      <div className="space-y-2 skill-demo-compact">
        {step.richCards.map((card, i) => (
          <div key={i} className="rounded-2xl rounded-tl-sm overflow-hidden shadow-sm">
            <RichCardRenderer card={card} />
          </div>
        ))}
      </div>
    </div>
  )
})

/* ── Typing dots indicator ── */
const BOUNCE_STYLE_0 = { animationDelay: "0ms" } as const
const BOUNCE_STYLE_1 = { animationDelay: "150ms" } as const
const BOUNCE_STYLE_2 = { animationDelay: "300ms" } as const

const TypingIndicator = memo(function TypingIndicator() {
  return (
    <div className="flex gap-2 animate-in fade-in duration-200">
      <DemoAgentAvatar />
      <div className="rounded-2xl rounded-tl-sm bg-secondary/50 border border-border/40 px-3.5 py-2.5 shadow-sm">
        <div className="flex gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={BOUNCE_STYLE_0} />
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={BOUNCE_STYLE_1} />
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={BOUNCE_STYLE_2} />
        </div>
      </div>
    </div>
  )
})

/* ── Step dispatcher ── */
const DemoStepRenderer = memo(function DemoStepRenderer({ step }: { step: SkillDemoStep }) {
  switch (step.type) {
    case "user":
      return <UserStep content={step.content} />
    case "agent-reply":
      return <AgentReplyStep content={step.content} />
    case "tool-calls":
      return <ToolCallsStep step={step} />
    case "rich-card":
      return <RichCardStep step={step} />
    default:
      return null
  }
})

const SIDEBAR_WIDTH = 420
const TRANSITION_DURATION = 320
const TRANSITION_TIMING = "cubic-bezier(0.16, 1, 0.3, 1)"

export const SkillDemoSidebar = memo(function SkillDemoSidebar({
  open,
  skillName,
  demoSteps = PLACEHOLDER_SKILL_DEMO,
  onClose,
}: {
  open: boolean
  skillName: string
  demoSteps?: SkillDemoStep[]
  onClose: () => void
}) {
  const [visibleCount, setVisibleCount] = useState(0)
  const [isStreaming, setIsStreaming] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  // Two-phase open: render in closed state first, then animate open on next frame
  const [hasOpened, setHasOpened] = useState(false)
  const [animateOpen, setAnimateOpen] = useState(false)
  useEffect(() => {
    if (open) {
      if (!hasOpened) {
        setHasOpened(true)
        const raf1 = requestAnimationFrame(() => {
          const raf2 = requestAnimationFrame(() => setAnimateOpen(true))
          timersRef.current.push(raf2 as unknown as ReturnType<typeof setTimeout>)
        })
        timersRef.current.push(raf1 as unknown as ReturnType<typeof setTimeout>)
      } else {
        setAnimateOpen(true)
      }
    } else {
      setAnimateOpen(false)
    }
  }, [open, hasOpened])

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
  }, [])

  useEffect(() => { scrollToBottom() }, [visibleCount, scrollToBottom])

  // Start/stop streaming when opened/closed
  useEffect(() => {
    if (!open) {
      timersRef.current.forEach(clearTimeout)
      timersRef.current = []
      const resetTimer = setTimeout(() => {
        setVisibleCount(0)
        setIsStreaming(false)
      }, TRANSITION_DURATION)
      return () => clearTimeout(resetTimer)
    }

    setIsStreaming(true)
    setVisibleCount(0)

    demoSteps.forEach((step, i) => {
      const t = setTimeout(() => {
        setVisibleCount(i + 1)
        if (i === demoSteps.length - 1) {
          const typingDuration = step.type === "agent-reply" && step.content
            ? step.content.length * 35 + 500
            : 500
          const t2 = setTimeout(() => setIsStreaming(false), typingDuration)
          timersRef.current.push(t2)
        }
      }, step.delay)
      timersRef.current.push(t)
    })

    return () => {
      timersRef.current.forEach(clearTimeout)
      timersRef.current = []
    }
  }, [open, demoSteps])

  // Memoize style objects to avoid re-creating on every render
  const outerStyle = useMemo(() => ({
    width: animateOpen ? SIDEBAR_WIDTH : 0,
    opacity: animateOpen ? 1 : 0,
    transitionDuration: `${TRANSITION_DURATION}ms`,
    transitionTimingFunction: TRANSITION_TIMING,
    clipPath: animateOpen ? "inset(-60px)" : "inset(0px 0px 0px 100%)",
  }), [animateOpen])

  const innerStyle = useMemo(() => ({
    width: SIDEBAR_WIDTH,
    transform: animateOpen ? "translateX(0)" : "translateX(-24px)",
    transitionDuration: `${TRANSITION_DURATION}ms`,
    transitionTimingFunction: TRANSITION_TIMING,
    pointerEvents: (animateOpen ? "auto" : "none") as React.CSSProperties["pointerEvents"],
  }), [animateOpen])

  // Derive visible steps from count + original array (no new array allocation per step)
  const visibleSteps = useMemo(
    () => demoSteps.slice(0, visibleCount),
    [demoSteps, visibleCount],
  )

  const showTypingDots = isStreaming
    && visibleCount > 0
    && demoSteps[visibleCount - 1]?.type === "user"

  if (!hasOpened) return null

  return (
    <div className="transition-[width,opacity,clip-path] max-h-[90vh]" style={outerStyle}>
      <div
        className="flex flex-col liquid-glass-panel rounded-2xl overflow-hidden h-full transition-transform"
        style={innerStyle}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-border/40 px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex items-center justify-center h-5 w-5 rounded-md bg-primary/10">
              <Loader2 className={`h-3 w-3 text-primary ${isStreaming ? "animate-spin" : "hidden"}`} />
              {!isStreaming && visibleCount > 0 && (
                <svg className="h-3 w-3 text-primary" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                  <path d="M8 0C8.5 5 11 8 16 8C11 8 8.5 11 8 16C7.5 11 5 8 0 8C5 8 7.5 5 8 0Z" />
                </svg>
              )}
            </div>
            <span className="text-sm font-medium text-foreground truncate">
              {skillName} 演示
            </span>
            {isStreaming && (
              <span className="text-micro text-muted-foreground animate-pulse">对话中...</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <PanelRightClose className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Chat area */}
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-3 no-scrollbar"
        >
          {visibleSteps.map((step, idx) => (
            <DemoStepRenderer key={idx} step={step} />
          ))}

          {showTypingDots && <TypingIndicator />}
        </div>
      </div>
    </div>
  )
})
